import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Copy, Check, ExternalLink, Loader2, AlertCircle, ShieldCheck } from "lucide-react";
import api from "../api";
import { useCart } from "../contexts/CartContext";
import useNoIndex from "../hooks/useNoIndex";

export default function VenmoPending() {
  const [params] = useSearchParams();
  const orderNumber = params.get("order");
  const autopay = params.get("autopay") === "1";
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [copied, setCopied] = useState({ handle: false, memo: false, total: false });
  const [autopayFired, setAutopayFired] = useState(false);
  const { clear } = useCart();
  useNoIndex();

  useEffect(() => {
    if (!orderNumber) {
      setState({ loading: false, error: "Missing order reference.", data: null });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/checkout/venmo-manual/${orderNumber}`);
        setState({ loading: false, error: null, data });
        clear();
        // Auto-launch Venmo (with amount + memo prefilled) if we arrived
        // straight from cart checkout. Universal link → opens app on mobile,
        // web Venmo on desktop. Delayed so the page paints first.
        if (autopay && data.venmo_pay_url) {
          setAutopayFired(true);
          // 2.5s delay so the customer reads the heads-up before Venmo opens
          setTimeout(() => {
            window.location.href = data.venmo_pay_url;
          }, 2500);
        }
      } catch (e) {
        setState({
          loading: false,
          error: e?.response?.data?.detail || "Unable to load order details.",
          data: null,
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  const copy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied((c) => ({ ...c, [key]: true }));
      setTimeout(() => setCopied((c) => ({ ...c, [key]: false })), 1800);
    } catch {
      /* noop */
    }
  };

  if (state.loading) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <div className="surface p-12">
          <Loader2 className="mx-auto text-[#00C3FF] animate-spin" size={36} />
          <div className="overline mt-6">LOADING</div>
          <h1 className="font-heading text-3xl mt-3">Preparing your Venmo instructions…</h1>
        </div>
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <div className="surface p-12">
          <AlertCircle className="mx-auto text-red-400" size={40} />
          <div className="overline mt-6">SOMETHING WENT WRONG</div>
          <h1 className="font-heading text-3xl mt-3">We couldn't load your order.</h1>
          <p className="text-sm text-[#8A94A6] mt-4">{state.error || "Please contact support."}</p>
          <Link to="/shop" className="btn-ghost mt-8 inline-flex" data-testid="venmo-pending-back-to-shop">
            Return to Shop
          </Link>
        </div>
      </div>
    );
  }

  const { order_number, total, venmo_handle, venmo_url, venmo_pay_url, memo } = state.data;
  const handleDisplay = `@${venmo_handle}`;
  const totalDisplay = `$${Number(total).toFixed(2)}`;
  const launchUrl = venmo_pay_url || venmo_url;

  // While we're about to auto-launch Venmo, show a focused warning screen
  // so the customer knows exactly what to tap (and what NOT to tap) when
  // Venmo opens. Removes the "Each / $X total" double-charge risk by
  // priming them to recognize the UI before they're in it.
  if (autopayFired) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20" data-testid="venmo-pending-handoff">
        <div className="surface p-8 sm:p-10 text-center">
          <div className="overline mb-3">OPENING VENMO IN A MOMENT…</div>
          <h1 className="font-heading text-3xl mt-2">Before Venmo opens, read this.</h1>

          <div className="mt-8 p-5 border-2 border-yellow-500/50 bg-yellow-500/5 text-left">
            <div className="overline text-yellow-400 mb-3">⚠ HEADS UP</div>
            <p className="text-sm text-white leading-relaxed">
              Venmo's pay screen will show <span className="font-mono-tech text-[#00C3FF]">{totalDisplay}</span>{" "}
              with the word <span className="font-mono-tech">"Each"</span> below it and a{" "}
              <span className="font-mono-tech">"${(Number(total) * 2).toFixed(2)} total"</span> figure underneath.
            </p>
            <p className="text-sm text-white leading-relaxed mt-3">
              This is just Venmo's default display for business accounts.{" "}
              <span className="text-yellow-300 font-medium">You will only be charged {totalDisplay} — once.</span>
            </p>
            <p className="text-xs text-[#8A94A6] leading-relaxed mt-4">
              ✅ Tap the big blue <span className="text-white">Pay</span> button when you're ready.
              <br />
              ❌ <span className="text-red-300">Do NOT</span> tap "Edit schedule" or change anything else.
            </p>
          </div>

          <div className="mt-6 p-4 border border-white/10 bg-[#0a0a14] text-left">
            <div className="overline text-[10px] mb-2">CONFIRM BEFORE TAPPING PAY</div>
            <div className="text-xs text-[#8A94A6] space-y-1.5">
              <div className="flex justify-between">
                <span>Recipient</span>
                <span className="font-mono-tech text-white">Helix Scientific Holdings</span>
              </div>
              <div className="flex justify-between">
                <span>Amount</span>
                <span className="font-mono-tech text-[#00C3FF]">{totalDisplay}</span>
              </div>
              <div className="flex justify-between">
                <span>Memo</span>
                <span className="font-mono-tech text-white">{memo}</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-[#5C657A] mt-6 font-mono-tech uppercase tracking-[0.18em]">
            Launching Venmo automatically in 2 seconds…
          </p>

          <a
            href={launchUrl}
            className="btn-primary w-full justify-center mt-4"
            data-testid="venmo-handoff-open-now"
          >
            Open Venmo Now <ExternalLink size={14} />
          </a>

          <button
            onClick={() => setAutopayFired(false)}
            className="text-[10px] font-mono-tech text-[#5C657A] hover:text-white uppercase tracking-[0.18em] mt-6"
            data-testid="venmo-handoff-back"
          >
            ← Show full instructions instead
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-20" data-testid="venmo-pending-page">
      <div className="surface p-8 sm:p-12">
        <div className="text-center">
          <ShieldCheck className="mx-auto text-[#00C3FF]" size={40} />
          <div className="overline mt-6" data-testid="venmo-pending-order-number">
            ORDER PLACED · {order_number}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl mt-3">
            Send your Venmo payment to complete the order.
          </h1>
          <p className="text-sm text-[#8A94A6] mt-4 max-w-md mx-auto">
            We've reserved your compounds. Once your Venmo payment lands, we ship within 24 hours
            and email tracking. The 2% Venmo discount is already applied below.
          </p>
        </div>

        <div className="mt-10 space-y-4">
          <CopyRow
            label="VENMO HANDLE"
            value={handleDisplay}
            onCopy={() => copy("handle", venmo_handle)}
            copied={copied.handle}
            testid="venmo-pending-handle"
          />
          <CopyRow
            label="AMOUNT TO SEND"
            value={totalDisplay}
            onCopy={() => copy("total", Number(total).toFixed(2))}
            copied={copied.total}
            testid="venmo-pending-total"
            highlight
          />
          <CopyRow
            label="MEMO / NOTE  ·  ORDER NUMBER ONLY"
            value={memo}
            onCopy={() => copy("memo", memo)}
            copied={copied.memo}
            testid="venmo-pending-memo"
          />
        </div>

        <div
          className="mt-6 p-5 border border-red-500/40 bg-red-500/5"
          data-testid="venmo-pending-compliance-warning"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={18} />
            <div>
              <div className="overline text-red-400 text-[10px]">
                IMPORTANT · COMPLIANCE
              </div>
              <p className="text-xs text-white leading-relaxed mt-2">
                Use <span className="font-mono-tech text-[#00C3FF]">{memo}</span> as the Venmo
                memo — <span className="text-red-300 font-medium">and nothing else</span>.
              </p>
              <p className="text-[11px] text-[#8A94A6] leading-relaxed mt-2">
                Do <span className="text-red-300 font-medium">NOT</span> type any product name,
                peptide name, abbreviation, or "ProSource" in the Venmo note. Orders with
                non-compliant memos will be <span className="text-white">refunded and cancelled</span>.
              </p>
            </div>
          </div>
        </div>

        <a
          href={launchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary w-full justify-center mt-8"
          data-testid="venmo-pending-open-venmo"
        >
          {autopayFired ? "Re-open Venmo" : "Open Venmo & Pay"} <ExternalLink size={14} />
        </a>

        {autopayFired && (
          <div
            className="mt-4 p-3 border border-[#00C3FF]/30 bg-[#00C3FF]/5 text-center"
            data-testid="venmo-pending-autopay-banner"
          >
            <p className="text-[11px] text-[#8A94A6] leading-relaxed">
              <span className="text-[#00C3FF] font-mono-tech uppercase tracking-wider text-[10px]">
                Launching Venmo…
              </span>{" "}
              If Venmo didn't open automatically, tap{" "}
              <span className="text-white">"Re-open Venmo"</span> above. Your amount and memo are
              pre-filled.
            </p>
          </div>
        )}

        <div className="mt-8 p-5 border border-white/10 bg-[#0a0a14]">
          <div className="overline mb-3">HOW THIS WORKS</div>
          <ol className="text-xs text-[#8A94A6] space-y-2 list-decimal pl-5 leading-relaxed">
            <li>Tap "Open Venmo & Pay" — Venmo opens with the recipient, amount, and memo all pre-filled.</li>
            <li>
              Confirm the memo says <span className="text-white font-mono-tech">{memo}</span> —{" "}
              <span className="text-red-300">no other text</span>. No product names. Just the
              order number.
            </li>
            <li>Tap <span className="text-white">Pay</span>. (Single one-time payment — do not tap "Edit schedule".)</li>
            <li>
              We manually verify each Venmo payment (usually within a few hours) — you'll get an
              "Order Confirmed" email the moment payment is matched. Then your compounds ship
              within 24 hours with tracking.
            </li>
          </ol>
        </div>

        <p className="text-[10px] text-[#5C657A] font-mono-tech text-center uppercase tracking-[0.2em] mt-8">
          For Research Use Only · Billed by Helix Scientific Holdings
        </p>

        <div className="text-center mt-6">
          <Link
            to="/account/orders"
            className="text-xs font-mono-tech text-[#00C3FF] hover:underline uppercase tracking-[0.18em]"
            data-testid="venmo-pending-view-orders"
          >
            View My Orders →
          </Link>
        </div>
      </div>
    </div>
  );
}

function CopyRow({ label, value, onCopy, copied, testid, highlight = false }) {
  return (
    <div
      className={`flex items-center justify-between gap-3 p-4 border ${highlight ? "border-[#00C3FF]/60 bg-[#00C3FF]/5" : "border-white/10"}`}
    >
      <div className="min-w-0">
        <div className="overline text-[10px]">{label}</div>
        <div
          className={`font-mono-tech mt-1 truncate ${highlight ? "text-2xl text-[#00C3FF]" : "text-base text-white"}`}
          data-testid={`${testid}-value`}
        >
          {value}
        </div>
      </div>
      <button
        onClick={onCopy}
        className="btn-ghost flex-shrink-0"
        style={{ padding: "10px 14px" }}
        data-testid={`${testid}-copy`}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}
